import * as Placeholder from '-/Custom/Placeholder';
import { Link } from 'expo-router';
import { StyleSheet } from 'react-native-unistyles';
import { useQuery, useSubscription } from 'urql';
import BadgeList from '@/components/Badge/BadgeList';
import BadgeListItem, {
  BadgeListItemFragment,
} from '@/components/Badge/BadgeListItem';
import ActionBar from '@/components/UI/ActionBar';
import ActionButton from '@/components/UI/ActionButton';
import Box from '@/components/UI/Box';
import Item from '@/components/UI/PressableRow';
import { type FragmentOf, graphql, readFragment } from '@/gql/graphql';

const EventDetailBadgeFragment = graphql(/* GraphQL */ `
  fragment EventDetailBadge on event {
    id
    root_owner {
      id
      title
      permissions
    }
  }
`);

const GetEventDetailBadge = graphql(/* GraphQL */ `
  query GetEventDetailBadge($eventId: uuid!) {
    event_by_pk(id: $eventId) {
      ...EventDetailBadge
    }
  }
`, [EventDetailBadgeFragment]);

const WatchEventDetailBadge = graphql(/* GraphQL */ `
  subscription WatchEventDetailBadge($eventId: uuid!) {
    event_by_pk(id: $eventId) {
      ...EventDetailBadge
    }
  }
`, [EventDetailBadgeFragment]);

export default function EventDetailBadge({ eventId }: { eventId: string }) {
  const [{ data }] = useQuery({
    query: GetEventDetailBadge,
    variables: { eventId: eventId || '' },
    pause: !eventId,
  });

  useSubscription({
    query: WatchEventDetailBadge,
    variables: { eventId: eventId || '' },
    pause: !eventId,
  });

  const event = readFragment(EventDetailBadgeFragment, data?.event_by_pk);
  const rootParent = event?.root_owner?.[0];

  return (
    <BadgeList
      ListHeaderComponent={
        rootParent?.permissions?.edit_badge ? (
          <ActionBar>
            <Link
              href={{
                pathname: '/badge/create',
                params: { eventId: rootParent.id },
              }}
              asChild
            >
              <ActionButton
                forceLabel
                label="Create Badge"
                icon="add-outline"
                variant="success"
              ></ActionButton>
            </Link>
          </ActionBar>
        ) : null
      }
      eventId={eventId}
      badgeAssigneesWhere={{ event_id: { _eq: eventId } }}
      renderItem={({
        item,
      }: {
        item: FragmentOf<typeof BadgeListItemFragment>;
      }) => {
        const badge = readFragment(BadgeListItemFragment, item);

        return (
          <Link
            href={{
              pathname: '/organization/badge/[badgeId]',
              params: { badgeId: badge.id, eventId },
            }}
            asChild
          >
            <Item>
              <BadgeListItem fragment={item} />
            </Item>
          </Link>
        );
      }}
      ListEmptyComponent={
        <Placeholder.Container>
          <Box style={style.icon}>
            <Placeholder.Icon name="grid-outline" />
          </Box>
          <Placeholder.Text>
            {rootParent?.title} Has No Badges.
          </Placeholder.Text>
          <Placeholder.Text>
            Badges can be used to track things like registration, waivers, or
            anything else you can think of.
          </Placeholder.Text>
        </Placeholder.Container>
      }
    />
  );
}

const style = StyleSheet.create({
  icon: {
    transform: [{ rotate: '45deg' }],
  },
});
